import cn from 'classnames';
import { Button } from '@Components/Button';
import { Appearance, Size } from '@Components/designConstants';
import { InteractiveSpan } from '@Components/InteractiveEl';
import { FilterFacet, Product } from './product-roadmap-constants';
import { distinctStatusBuckets, FacetSelection } from './product-roadmap-utils';

interface ProductRoadmapStatusBarProps {
  products: Product[];
  scopedProducts: Product[];
  selection: FacetSelection;
  totalCount: number;
  onChange: (next: ReadonlySet<string>) => void;
  onReset: () => void;
}

// The top status strip: colored toggle chips, one per canonical status bucket (not per raw
// `life` synonym -- Deployed/Live/Shipped/Shipping all collapse to one "Deployed" chip), with
// live counts. Matches the retired HTML prototype's "statusbar". Function/Kind/Scope/Audience
// filtering lives in the table's own column headers instead (see ProductRoadmapTable).
export const ProductRoadmapStatusBar = ({
  products,
  scopedProducts,
  selection,
  totalCount,
  onChange,
  onReset
}: ProductRoadmapStatusBarProps) => {
  const selected = selection[FilterFacet.STATUS];
  const options = distinctStatusBuckets(scopedProducts);
  const anySelected = Object.values(selection).some(s => s.size > 0);

  // Buckets can carry more than one raw `life` synonym -- toggling always acts on every raw
  // value in the bucket at once, so a bucket is never left half-selected.
  const toggle = (bucketKey: string) => {
    const wasUnfiltered = selected.size === 0;
    const clicked = options.find(o => o.bucket.key === bucketKey);
    const active = wasUnfiltered || Boolean(clicked?.rawValues.every(v => selected.has(v)));
    const next = new Set(selected);

    if (active) {
      clicked?.rawValues.forEach(v => next.delete(v));
      // Nothing was explicitly selected before this click (every bucket showing) -- turning one
      // off means every *other* bucket must become explicit, or an empty Set would fall back to
      // "no filter = show all" and undo the click.
      if (wasUnfiltered) {
        options
          .filter(o => o.bucket.key !== bucketKey)
          .forEach(o => o.rawValues.forEach(v => next.add(v)));
      }
    } else {
      clicked?.rawValues.forEach(v => next.add(v));
    }
    onChange(next);
  };

  return (
    <div className='product-roadmap-status-bar'>
      <span className='product-roadmap-status-bar-label'>Status</span>
      <div className='product-roadmap-status-bar-chips' role='group' aria-label='Status filter'>
        {options.map(({ bucket, rawValues, count }) => {
          const active = selected.size === 0 || rawValues.every(v => selected.has(v));
          return (
            <InteractiveSpan
              key={bucket.key}
              className={cn('status-bar-chip', {
                [`design-${bucket.style.design}`]: bucket.style.design,
                [`intent-${bucket.style.intent}`]: bucket.style.intent,
                [`custom-${bucket.style.custom}`]: bucket.style.custom,
                'is-off': !active
              })}
              aria-pressed={active}
              onClick={() => toggle(bucket.key)}
            >
              <span className='status-bar-chip-dot' />
              <span className='status-bar-chip-label'>{bucket.label}</span>
              <span className='status-bar-chip-count'>{count}</span>
            </InteractiveSpan>
          );
        })}
      </div>
      <div className='product-roadmap-status-bar-right'>
        <span className='product-roadmap-status-bar-count'>
          {products.length} of {totalCount} products
        </span>
        <Button
          appearance={Appearance.EMPTY}
          size={Size.SM}
          disabled={!anySelected}
          onClick={onReset}
        >
          Clear all filters
        </Button>
      </div>
    </div>
  );
};
